import React from 'react';
import {
    Col,
    FormGroup,
    List,
} from 'reactstrap';
import { debounce } from 'lodash-es';
import AsyncSelect from 'react-select/async';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import {
    AppPageForm, FormCheckbox, FormLabel, onFieldChange, onReactSelectChanged, toasty,
} from '../common/forms/FormElements';
import CustomCircularProgress from '../common/CustomCircularProgress';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { YesNoButtons } from '../common/forms/YesNoButtons';
import { TipAlert } from '../common/TipAlert';
import { Block, BlockBorder } from '../common/Block';
import { SaveButton } from '../common/forms/SaveButton';
import { _ContractChargeTypeEligibility } from '../chargeType/ChargeType';
import { ExecutionHistory } from './common/ExecutionHistory';

const Ordering = {
    FIRST: 'First',
    LAST: 'Last',
};

const ChargeTypeEligibilityLabels = {
    [_ContractChargeTypeEligibility.Required]: 'Required',
    [_ContractChargeTypeEligibility.Optional]: 'Optional',
    [_ContractChargeTypeEligibility.DoNotUse]: 'Do Not Use',
};

const ChargeTypeEligibilityOptions = [
    { value: _ContractChargeTypeEligibility.DoNotUse, label: 'Do Not Use' },
    { value: _ContractChargeTypeEligibility.Optional, label: 'Optional' },
    { value: _ContractChargeTypeEligibility.Required, label: 'Required' },
];

class AddChargeTypeToContractsViewModel {
    chargeTypeId = '';
    ordering = Ordering.FIRST;
    tenantId = null;
    isProjectContract = false;
    chargeTypeEligibilityId = _ContractChargeTypeEligibility.DoNotUse;
    excludedContractIds = [];
}

class AddChargeTypeToContracts extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            formValidated: false,
            loading: false,
            saving: false,
            data: new AddChargeTypeToContractsViewModel(),
            isValidated: false,
            tenants: [],
            selectedTenant: null,
            chargeTypeOptions: [],
            selectedExcludedContracts: [],
        };

        this.onChange = this.onChange.bind(this);
        this.onReactSelectChanged = onReactSelectChanged.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    populateState = async () => {
        const [tenants] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.tenants()),
        ]);

        this.setState({
            tenants,
        });
    };

    onChange = onFieldChange;

    onSubmit = async () => {
        const { data } = this.state;

        if ((data.excludedContractIds ?? []).length > 0 === false && this.state.selectedExcludedContracts.length > 0) {
            data.excludedContractIds = this.state.selectedExcludedContracts.map((x) => x.value);
        }

        this.setState({ saving: true });

        try {
            await httpClient.post(
                ApiRoutes.adminTool.addChargeTypeToContracts(),
                data,
            );

            this.setState({
                data: new AddChargeTypeToContractsViewModel(),
                isValidated: false,
                selectedTenant: null,
                chargeTypeOptions: [],
                selectedExcludedContracts: [],
            });

            toasty.success('The charge type has been added to all flagging contracts in the selected tenant.');

            // Refresh the table after successful submission
            await this.props.refreshUsageHistory();
        } catch (err) {
            console.error(err);
            toasty.error('Error adding charge type');
            await this.props.refreshUsageHistory();
        } finally {
            this.setState({ saving: false });
        }
    };

    onTenantChanged = (selectedOption) => {
        this.setState((prevState) => ({
            selectedTenant: selectedOption,
            chargeTypeOptions: [],
            data: {
                ...prevState.data,
                chargeTypeId: null,
                tenantId: selectedOption ? selectedOption.value : null,
                excludedContractIds: [],
            },
            selectedExcludedContracts: [],
        }));
    };

    loadOptions = debounce((inputValue, callback) => {
        const { tenantId } = this.state.data;

        if (tenantId) {
            fetch(ApiRoutes.typeAheads.chargeTypeSearchByTenant(inputValue, tenantId))
                .then((response) => response.json())
                .then((data) => {
                    this.setState({ chargeTypeOptions: data });
                    callback(data);
                });
        }
    }, 250);

    loadContractOptions = debounce((inputValue, callback) => {
        const { tenantId } = this.state.data;

        if (tenantId) {
            httpClient
                .js(ApiRoutes.typeAheads.contractsSearchByTenant(tenantId, inputValue))
                .then((options) => callback(options));
        } else {
            callback([]);
        }
    }, 250);

    render() {
        const {
            loading,
            saving,
            isValidated,
            tenants,
            selectedTenant,
            chargeTypeOptions,
            data,
            selectedExcludedContracts,
        } = this.state;

        if (loading) {
            return (
                <div className="confirmationContainer pt-2 d-flex flex-row align-items-center justify-content-center">
                    <CustomCircularProgress
                        color="secondary"
                        variant="indeterminate"
                        label={
                            this.props.match.params.id
                                ? 'Getting Tool History...'
                                : 'Loading form...'
                        }
                    />
                </div>
            );
        }

        if (!(this.context ?? {}).user) return null;

        return (
            <AppPageForm
                backUrl={AppNavPaths.AdminTool}
                backLabel="Back to Admin Tools"
                formShown={this.context.formIsOpen}
                formId="adminToolList"
                formName="adminToolList"
                formHeading="Add Charge Type to Flagging Contracts"
                formRef={this.formRef}
                formIsColumn
                saving={saving}
                isValidated={isValidated}
                onSubmit={this.onSubmit}
                setIsValidated={(value) => {
                    this.setState({ isValidated: value });
                }}
            >
                <Block padding="medium">
                    <div>
                        <ValidatedSelect
                            id="selectedTenant"
                            name="data.selectedTenant"
                            label="Select a tenant"
                            required
                            options={tenants}
                            value={selectedTenant}
                            onChange={this.onTenantChanged}
                            error="Select a tenant to add "
                        />

                        <ValidatedSelect
                            name="data.chargeTypeId"
                            label="Charge Type"
                            component={AsyncSelect}
                            loadOptions={this.loadOptions}
                            placeholder="Type Charge Type Here"
                            id="chargeTypeId"
                            value={
                                (chargeTypeOptions ?? []).find((x) => x.value === data.chargeTypeId) ?? null
                            }
                            onChange={this.onReactSelectChanged}
                            error="A charge type selection is required"
                            isClearable
                            cacheOptions
                            required
                        />

                        <Col
                            xs={6}
                            className="p-0"
                        >
                            <YesNoButtons
                                className="d-flex"
                                label="Where should this appear within each contract's charge types?"
                                name="ordering"
                                value={data.ordering}
                                required
                                onClick={(value) => {
                                    data.ordering = value;
                                    this.setState({ data });
                                }}
                                options={[
                                    { value: Ordering.FIRST, label: 'First' },
                                    { value: Ordering.LAST, label: 'Last' },
                                ]}
                            />
                        </Col>

                        <ValidatedSelect
                            id="chargeTypeEligibilityId"
                            name="data.chargeTypeEligibilityId"
                            label="Contract Charge Type Eligibility"
                            classNamePrefix="react-select"
                            className="smallReactSelect"
                            formGroupClassName="mt-3"
                            options={ChargeTypeEligibilityOptions}
                            value={ChargeTypeEligibilityOptions.find((x) => x.value === data.chargeTypeEligibilityId) ?? null}
                            onChange={(option) => {
                                this.setState((prevState) => ({
                                    data: {
                                        ...prevState.data,
                                        chargeTypeEligibilityId: option?.value ?? _ContractChargeTypeEligibility.DoNotUse,
                                    },
                                }));
                            }}
                            required
                            error="A contract charge type eligibility is required"
                        />

                        <FormGroup className="mt-3">
                            <FormLabel
                                label="Contract Applicability"
                            />

                            <TipAlert
                                label="If this is left unchecked, this update will apply to all contracts. Rental charges will only ever be added to rental contracts regardless of what's selected."
                            />

                            <FormCheckbox
                                small
                                id="data.isProjectContract"
                                name="data.isProjectContract"
                                checked={data.isProjectContract}
                                onChange={this.onChange}
                                labelText="Restrict this update to only rental contracts"
                            />
                        </FormGroup>

                        <FormGroup className="mt-3">
                            <FormLabel label="Exclude Contracts" />

                            <TipAlert
                                label="Optionally exclude specific contracts from the selected tenant so the update applies to all others."
                            />

                            <ValidatedSelect
                                name="excludedContractIds"
                                label="Contracts to Exclude"
                                component={AsyncSelect}
                                loadOptions={this.loadContractOptions}
                                placeholder="Type Contract Number Here"
                                id="excludedContractIds"
                                value={selectedExcludedContracts}
                                onChange={(selectedOptions) => {
                                    this.setState((prevState) => ({
                                        selectedExcludedContracts: selectedOptions ?? [],
                                        data: {
                                            ...prevState.data,
                                            excludedContractIds: (selectedOptions ?? []).map((x) => x.value),
                                        },
                                    }));
                                }}
                                isClearable
                                isMulti
                                cacheOptions
                                isDisabled={!data.tenantId}
                            />
                        </FormGroup>

                        <BlockBorder />

                        <div className="d-flex justify-content-center">
                            <SaveButton
                                saving={saving}
                                label="Submit"
                            />
                        </div>
                    </div>
                </Block>

                <ExecutionHistory usageHistory={this.props.usageHistory}>
                    {(record) => (
                        <List type="unstyled">
                            {record.input.chargeType && (
                                <li>
                                    <strong>Charge Type:</strong>
                                    {' '}
                                    {record.input.chargeType}
                                </li>
                            )}

                            {record.input.tenantId && (
                                <li>
                                    <strong>Tenant ID:</strong>
                                    {' '}
                                    {record.input.tenantId}
                                </li>
                            )}

                            {record.input.tenantName && (
                                <li>
                                    <strong>Tenant:</strong>
                                    {' '}
                                    {record.input.tenantName}
                                </li>
                            )}

                            {record.input.ordering && (
                                <li>
                                    <strong>Order:</strong>
                                    {' '}
                                    {record.input.ordering}
                                </li>
                            )}

                            {record.input.rentalsOnly && (
                                <li>
                                    <strong>Applies only to rentals:</strong>
                                    {' '}
                                    {record.input.rentalsOnly}
                                </li>
                            )}

                            {record.input.chargeTypeEligibilityId && (
                                <li>
                                    <strong>Eligibility:</strong>
                                    {' '}
                                    {ChargeTypeEligibilityLabels[record.input.chargeTypeEligibilityId]}
                                </li>
                            )}

                            {Array.isArray(record.input.excludedContractIds) && record.input.excludedContractIds.length > 0 && (
                                <li>
                                    <strong>Excluded Contract IDs:</strong>
                                    {' '}
                                    {record.input.excludedContractIds.join(', ')}
                                </li>
                            )}

                            {record.output.contracts && (
                                <li>
                                    <strong>Contracts Affected:</strong>
                                    {' '}
                                    {record.output.contracts}
                                </li>
                            )}
                        </List>
                    )}
                </ExecutionHistory>
            </AppPageForm>
        );
    }
}

export default AddChargeTypeToContracts;
